/**
 * AnchorNav 树形选择 + 同 Guid 相邻合并纯函数（F13，ADR-0014）。
 *
 * **selectAnchorNavTree**：从 aggregate NavItem 树筛选要在 navbox 渲染的一级项
 *   （用户确认 Q4「有 children 或命中首页锚点或 Guid 直跳」）：
 *   - 有非空 children → 保留下拉父项（自身点击仅切换下拉，不导航）
 *   - 无 children + 自身 targetAnchor：
 *     · home-anchor 命中首页区（`#type` ∈ sectionAnchorIds）→ 保留直链
 *     · home-anchor 不命中 → 丢弃 + console.warn（配置笔误，无对应区可滚）
 *     · detail（Guid / `<guid>#anchor`）→ 保留详情直跳
 *     · module（`module:<route>...`）→ 保留模块页直跳
 *     · invalid → 丢弃 + console.error（配置错误，Debug-First 暴露）
 *   仅保留 parentId=null 的一级项（aggregate 已是树形，过滤作健壮兜底）。
 *
 * **resolveChildren**：把二级 children 解析为 `ResolvedChild[]`，对相邻同 Guid
 *   detail 项合并（issue「相邻同 Guid NavItem 合并为一个详情入口」）：
 *   - 连续同 sectionId 的 detail children → 合并为一条，displayName 取首个，
 *     target anchor 改为 `MATERIALS_FOCUS_ANCHOR`（点击聚焦详情页 Material 区）
 *   - 单个 detail child → 保留原 target（含 anchor）
 *   - home-anchor / invalid child → 各自独立，不参与合并
 *   - 同 Guid 但不相邻（中间夹其他项）→ 不合并
 *
 * 纯函数 + readonly 入参，无 Vue / DOM / router 依赖，便于单测。
 */
import type { NavItemView } from '@/api/types';
import { parseTarget, MATERIALS_FOCUS_ANCHOR, type NavTarget } from '@/composables/nav-target';

const TOP_LEVEL_PARENT_ID = null;

/** 二级项解析结果：携带稳定 key + 显示名 + 解析后 target + 是否合并标记。 */
export interface ResolvedChild {
  readonly key: string;
  readonly displayName: string;
  readonly target: NavTarget;
  readonly merged: boolean;
}

/**
 * 筛选 navbox 渲染的一级项。`sectionAnchorIds` 为首页区锚点 id 集合
 * （type 字符串，如 `['problem','video']`）。返回保留的一级 NavItem（含 children）。
 */
export function selectAnchorNavTree(
  navItems: readonly NavItemView[],
  sectionAnchorIds: readonly string[],
): readonly NavItemView[] {
  const available = new Set(sectionAnchorIds);
  return navItems.filter((item) => shouldKeepTopLevel(item, available));
}

function shouldKeepTopLevel(item: NavItemView, available: ReadonlySet<string>): boolean {
  if (item.parentId !== TOP_LEVEL_PARENT_ID) {
    return false;
  }
  if ((item.children?.length ?? 0) > 0) {
    return true;
  }
  const target = parseTarget(item.targetAnchor);
  if (target.kind === 'home-anchor') {
    if (available.has(target.id)) {
      return true;
    }
    // eslint-disable-next-line no-console
    console.warn(
      `[AnchorNav] nav item target does not match a home section: "${item.targetAnchor}"`,
    );
    return false;
  }
  if (target.kind === 'detail' || target.kind === 'module') {
    return true;
  }
  // invalid 顶层项保留渲染：点击时 AnchorNav navigate() 显可见错误提示
  // + console.error（issue「可见的导航失败提示，不静默无响应」），不在此丢弃。
  return true;
}

/**
 * 解析二级 children 为 ResolvedChild[]，合并相邻同 Guid detail 项。
 * children 应已按 sort 升序（aggregate 投影保证）。
 */
export function resolveChildren(
  children: readonly NavItemView[] | undefined,
): readonly ResolvedChild[] {
  const list = children ?? [];
  const result: ResolvedChild[] = [];
  let i = 0;
  while (i < list.length) {
    const current = list[i]!;
    const target = parseTarget(current.targetAnchor);
    if (target.kind === 'detail') {
      let j = i + 1;
      while (j < list.length && sameDetailSection(parseTarget(list[j]!.targetAnchor), target.sectionId)) {
        j += 1;
      }
      const groupSize = j - i;
      if (groupSize > 1) {
        result.push({
          key: current.id,
          displayName: current.displayName,
          target: { kind: 'detail', sectionId: target.sectionId, anchor: MATERIALS_FOCUS_ANCHOR },
          merged: true,
        });
      } else {
        result.push({
          key: current.id,
          displayName: current.displayName,
          target,
          merged: false,
        });
      }
      i = j;
      continue;
    }
    result.push({
      key: current.id,
      displayName: current.displayName,
      target,
      merged: false,
    });
    i += 1;
  }
  return result;
}

function sameDetailSection(target: NavTarget, sectionId: string): boolean {
  return target.kind === 'detail' && target.sectionId === sectionId;
}
